import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

function Layout2({
  loading,
  heading,
  Route,
  btnName,
  Data,
  setData,
  Icons,
  messages,
  DeleteFunc,
}) {
  const theme = useTheme();

  let navigate = useNavigate();

  const [deleteDialog, setDeleteDialog] = useState(null);

 const handleDeleteConfirm = async () => {
  try {
    await DeleteFunc(deleteDialog); 
    setData((prev) => prev.filter((a) => a.id !== deleteDialog));
    toast.success(`${messages[3]} deleted successfully`);
  } catch (e) {
    toast.error("Error deleting the item.");
  } finally {
    setDeleteDialog(null);
  }
};

  return (
    <Box p={4} minHeight={"100vh"}>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" fontWeight={600}>
              {heading}
            </Typography>
            {Data?.length > 0 && (
              <Button
                variant="contained"
                onClick={() => navigate(`${Route}/add`)}
                sx={{ borderRadius: 2 }}
              >
                {btnName}
              </Button>
            )}
          </Box>

          {Data?.length === 0 ? (
            <Card
              elevation={2}
              sx={{
                textAlign: "center",
                p: 5,
                maxWidth: 500,
                mx: "auto",
                mt: 10,
                borderRadius: 3,
              }}
            >
              {Icons[0]}
              <Typography variant="h6" gutterBottom>
                No {messages[0]} added yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Get started by adding your first {messages[1]}.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate(`${Route}/add`)}
              >
                {messages[2]}
              </Button>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {Data?.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.background.paper
                          : theme.palette.grey[100],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0px 4px 12px rgba(255, 255, 255, 0.1)"
                          : "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transition: "transform 0.2s ease-in-out, box-shadow 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0px 6px 18px rgba(255, 255, 255, 0.15)"
                            : "0px 6px 18px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        {Icons[1]}
                        <Typography variant="h6" fontWeight={600}>
                          {item.name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {Icons[2]}
                        <Typography
                          variant="body2"
                          sx={{ marginBottom: "-8px !important" }}
                          color="text.secondary"
                        >
                          {item.description}
                        </Typography>
                      </Stack>

                       <Stack direction="row" spacing={1} alignItems="center">
                        {Icons[3]}
                        <Typography
                          variant="body2"
                          sx={{ marginBottom: "-8px !important" }}
                          color="text.secondary"
                        >
                          {item.info}
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          fullWidth
                          onClick={() => navigate(`${Route}/edit/${item.id}`)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          fullWidth
                          onClick={() => setDeleteDialog(item.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Delete Confirmation */}
          <Dialog
            open={!!deleteDialog}
            onClose={() => setDeleteDialog(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(47, 54, 69, 0.14)",
              },
            }}
          >
            <DialogTitle
              sx={{
                m: 0,
                px: 2.5,
                py: 2,
                backgroundColor: "#fff",
                color: "#0891B2",
                borderBottom: "1px solid #e8eaef",
                fontWeight: 600,
                fontSize: "1.05rem",
              }}
            >
              Confirm Delete
            </DialogTitle>
            <DialogContent sx={{ p: 3, backgroundColor: "#fff" }}>
              <Typography sx={{ color: "#4b5563", lineHeight: 1.6 }}>
                Are you sure you want to permanently delete this {messages[3]}?
                This action cannot be undone. This will also delete all the
                related data.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                p: 3,
                pt: 0,
                backgroundColor: "#fff",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setDeleteDialog(null)}
                sx={{
                  color: "#6b7280",
                  borderColor: "#d1d5db",
                  minWidth: 100,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "9px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDeleteConfirm}
                sx={{
                  backgroundColor: "#0891B2",
                  color: "#fff",
                  minWidth: 100,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "9px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#077d9c",
                    boxShadow: "none",
                  },
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default Layout2;
